// Designer G5 — auto-layout engine (Figma-style). A container node carries
// props.layout { mode:'row'|'column', gap, pad, align }; its direct
// children are positioned automatically along the main axis and aligned on
// the cross axis. The container "hugs" its content (size = content + pad).
// Pure + recursive (a child that is itself a layout container contributes
// its computed size). Mirrored server-side in view-html.ts for the View SDK.

import type { DesignerNode } from './opmodel';

export interface LayoutSpec {
  // 'grid' = dashboard-style N-column grid; top-level nodes are placed by
  // their `grid` cell (x,y,w,h in cells), unlike the free `rect` modes.
  mode: 'row' | 'column' | 'none' | 'grid';
  gap?: number;
  pad?: number;
  align?: 'start' | 'center' | 'end' | 'stretch';
  /** Main-axis distribution when the container is fixed-size (extra space). */
  justify?: 'start' | 'center' | 'end' | 'between';
  /** Per-axis sizing: 'hug' (size to content, default) or 'fixed' (use rect). */
  wMode?: 'hug' | 'fixed';
  hMode?: 'hug' | 'fixed';
  /** Grid mode: number of columns (default 12) and px height of one row. */
  cols?: number;
  rowH?: number;
  /** Grid mode: independent column/row gaps (px). Fall back to `gap`. */
  rowGap?: number;
  colGap?: number;
  /** Grid mode: whether newly dropped elements are wrapped in a titled panel
   *  (card chrome) by default, vs placed bare. Default true. Per-element
   *  override lives in `node.props.panel.show`. */
  panelDefault?: boolean;
}

/** Per-node panel chrome (grid mode): a card + title header around the element.
 *  `show:false` renders the element bare. Stored in `node.props.panel`. */
export interface PanelSpec {
  show?: boolean;
  title?: string;
  align?: 'left' | 'center' | 'right';
  titleColor?: string;
  bg?: string;
  showType?: boolean;
}
export interface Size { w: number; h: number }
export interface Rect { x: number; y: number; w: number; h: number }

// ── Grid layout (dashboard-style) ──────────────────────────────────────
// A unified-editor "dashboard" sets root.props.layout.mode='grid'. Top-level
// nodes carry `grid` {x,y,w,h} in CELL units; we map cells↔px against the
// frame width so the existing rect-based rendering/drag/resize machinery is
// reused (the px rect is derived, never stored, in grid mode).

export interface GridSpec { cols: number; rowH: number; gap: number; rowGap: number; colGap: number }
export interface GridCell { x: number; y: number; w: number; h: number }

export function gridSpecOf(l: LayoutSpec | Record<string, unknown> | null | undefined): GridSpec {
  const g = l as { cols?: number; rowH?: number; gap?: number; rowGap?: number; colGap?: number } | null | undefined;
  const gap = g?.gap ?? 8;
  return { cols: g?.cols ?? 12, rowH: g?.rowH ?? 40, gap, rowGap: g?.rowGap ?? gap, colGap: g?.colGap ?? gap };
}

/** Width of a single column in px for the given frame width. */
export function gridCellW(spec: GridSpec, frameW: number): number {
  return (frameW - spec.gap * (spec.cols - 1)) / spec.cols;
}

/** Cell placement → px rect within the frame. */
export function gridCellToRect(cell: GridCell, spec: GridSpec, frameW: number): Rect {
  const cw = gridCellW(spec, frameW);
  return {
    x: Math.round(cell.x * (cw + spec.gap)),
    y: Math.round(cell.y * (spec.rowH + spec.gap)),
    w: Math.round(cell.w * cw + (cell.w - 1) * spec.gap),
    h: Math.round(cell.h * spec.rowH + (cell.h - 1) * spec.gap),
  };
}

/** Nearest cell for a px rect, clamped so x+w never exceeds the columns. */
export function rectToGridCell(rect: Rect, spec: GridSpec, frameW: number): GridCell {
  const cw = gridCellW(spec, frameW);
  const w = Math.max(1, Math.min(spec.cols, Math.round((rect.w + spec.gap) / (cw + spec.gap))));
  const h = Math.max(1, Math.round((rect.h + spec.gap) / (spec.rowH + spec.gap)));
  const x = Math.max(0, Math.min(spec.cols - w, Math.round(rect.x / (cw + spec.gap))));
  const y = Math.max(0, Math.round(rect.y / (spec.rowH + spec.gap)));
  return { x, y, w, h };
}

export function layoutOf(n: DesignerNode): LayoutSpec | null {
  const l = (n.props as { layout?: LayoutSpec } | undefined)?.layout;
  return l && l.mode && l.mode !== 'none' ? l : null;
}

/** A node's intrinsic size: its own rect, or its computed size if it's a
 *  (non-empty) layout container. */
export function intrinsicSize(n: DesignerNode): Size {
  if (layoutOf(n) && (n.children?.length ?? 0) > 0) return computeLayout(n).size;
  const r = n.rect ?? { w: 80, h: 60 };
  return { w: r.w, h: r.h };
}

/** Compute a layout container's hugged size + each direct child's rect
 *  (relative to the container box). */
interface Margin { t: number; r: number; b: number; l: number }
function marginOf(c: DesignerNode): Margin {
  const m = (c.props as { margin?: { t?: number; r?: number; b?: number; l?: number } } | undefined)?.margin ?? {};
  return { t: m.t ?? 0, r: m.r ?? 0, b: m.b ?? 0, l: m.l ?? 0 };
}

export function computeLayout(n: DesignerNode): { size: Size; rects: Record<string, Rect> } {
  const l = layoutOf(n)!;
  const pad = l.pad ?? 8, gap = l.gap ?? 8, row = l.mode === 'row';
  const kids = n.children ?? [];
  const sizes = kids.map(intrinsicSize);
  const margins = kids.map(marginOf);
  // Per-child main/cross margin totals + leading edges (direction-aware).
  const mainM = (i: number) => (row ? margins[i]!.l + margins[i]!.r : margins[i]!.t + margins[i]!.b);
  const crossM = (i: number) => (row ? margins[i]!.t + margins[i]!.b : margins[i]!.l + margins[i]!.r);
  const mainLead = (i: number) => (row ? margins[i]!.l : margins[i]!.t);
  const mainTrail = (i: number) => (row ? margins[i]!.r : margins[i]!.b);
  const crossLead = (i: number) => (row ? margins[i]!.t : margins[i]!.l);
  const innerCross = sizes.length ? Math.max(...sizes.map((s, i) => (row ? s.h : s.w) + crossM(i))) : 0;
  const innerContent = sizes.reduce((sum, s, i) => sum + (row ? s.w : s.h) + mainM(i), 0) + (kids.length > 1 ? (kids.length - 1) * gap : 0);
  const hugMain = innerContent + 2 * pad;
  const hugCross = innerCross + 2 * pad;

  // Fixed axes use the container's own rect; hug axes use the content.
  const r = n.rect ?? { w: hugMain, h: hugCross };
  const mainFixed = row ? l.wMode === 'fixed' : l.hMode === 'fixed';
  const crossFixed = row ? l.hMode === 'fixed' : l.wMode === 'fixed';
  const mainExtent = mainFixed ? (row ? r.w : r.h) : hugMain;
  const crossExtent = crossFixed ? (row ? r.h : r.w) : hugCross;
  const innerCrossExtent = crossExtent - 2 * pad;

  // Distribute extra main-axis space (only when fixed & larger than content).
  const freeInner = Math.max(0, mainExtent - hugMain);
  // Grow children share the free main-axis space; if any grow, justify is moot.
  const grow = (c: DesignerNode) => (c.props as { grow?: boolean } | undefined)?.grow === true;
  const growCount = kids.filter(grow).length;
  const growExtra = growCount && freeInner > 0 ? freeInner / growCount : 0;
  const j = l.justify ?? 'start';
  let start = pad, gap2 = gap;
  if (freeInner > 0 && growExtra === 0) {
    if (j === 'center') start = pad + freeInner / 2;
    else if (j === 'end') start = pad + freeInner;
    else if (j === 'between' && kids.length > 1) gap2 = gap + freeInner / (kids.length - 1);
  }

  const rects: Record<string, Rect> = {};
  let main = start;
  kids.forEach((c, i) => {
    const s = sizes[i]!;
    const cross = row ? s.h : s.w;
    const mainSize = (row ? s.w : s.h) + (grow(c) ? growExtra : 0);
    main += mainLead(i); // leading margin on the main axis
    const cLead = crossLead(i);
    const availCross = innerCrossExtent - crossM(i); // cross space minus this child's margins
    let off = pad + cLead;
    let crossSize = cross;
    // Per-child align-self overrides the container's cross-axis alignment.
    const self = (c.props as { alignSelf?: string } | undefined)?.alignSelf;
    const a = self && self !== 'auto' ? self : l.align;
    if (a === 'center') off = pad + cLead + (availCross - cross) / 2;
    else if (a === 'end') off = pad + cLead + (availCross - cross);
    else if (a === 'stretch') crossSize = availCross; // fill cross axis (minus margins)
    rects[c.id] = row ? { x: main, y: off, w: mainSize, h: crossSize } : { x: off, y: main, w: crossSize, h: mainSize };
    main += mainSize + mainTrail(i) + gap2; // trailing margin + gap
  });

  const size: Size = row ? { w: mainExtent, h: crossExtent } : { w: crossExtent, h: mainExtent };
  return { size, rects };
}
